import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { validateDateRange } from '../../../utils/DateUtils';

// Утилита дебаунсинга
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

export const useChartState = (
  title,
  vehicle,
  startDate,
  endDate,
  data,
  labels,
  color,
  backgroundGradient,
  fetchData
) => {
  // Основные состояния
  const [expandedMode, setExpandedMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showReportChooser, setShowReportChooser] = useState(false);
  const [splitDirection, setSplitDirection] = useState(null);
  const [containerToFill, setContainerToFill] = useState(null);
  const [syncGroupId, setSyncGroupId] = useState(null);
  const [localStorageVehicle, setLocalStorageVehicle] = useState(null);
  const [localStorageDateRange, setLocalStorageDateRange] = useState(null);

  // Рефы
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);
  const containerId = useRef(`chart-container-${Math.random().toString(36).substring(2, 11)}`).current;
  
  // Реф для отслеживания активности загрузки и предотвращения множественных запросов
  const loadingRef = useRef(false);
  const lastLoadParamsRef = useRef(null);

  // Функция для получения данных из localStorage
  const getLocalStorageData = useCallback(() => {
    try {
      // Получаем данные о транспортном средстве
      const storedVehicle = localStorage.getItem('lastSelectedVehicle');
      if (storedVehicle) {
        try {
          const parsedVehicle = JSON.parse(storedVehicle);
          console.log(`BaseChart: Получены новые данные о ТС из localStorage:`, {
            id: parsedVehicle.id,
            name: parsedVehicle.name,
            imei: parsedVehicle.imei
          });
          setLocalStorageVehicle(parsedVehicle);
        } catch (e) {
          console.error('BaseChart: Ошибка при разборе данных о транспорте из localStorage:', e);
        }
      }

      // Получаем данные о диапазоне дат
      const storedDateRange = localStorage.getItem('lastDateRange');
      if (storedDateRange) {
        try {
          const parsedDateRange = JSON.parse(storedDateRange);
          if (parsedDateRange.startDate && parsedDateRange.endDate) {
            console.log(`BaseChart: Получены новые данные о диапазоне дат из localStorage:`, {
              startDate: parsedDateRange.startDate,
              endDate: parsedDateRange.endDate
            });
            setLocalStorageDateRange(parsedDateRange);
          }
        } catch (e) {
          console.error('BaseChart: Ошибка при разборе данных о диапазоне дат из localStorage:', e);
        }
      }
    } catch (error) {
      console.error('BaseChart: Ошибка при чтении данных из localStorage:', error);
    }
  }, []);

  // Эффект для загрузки данных из localStorage при монтировании
  useEffect(() => {
    getLocalStorageData();
  }, [getLocalStorageData]);

  // Обработчик изменений в localStorage
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'lastSelectedVehicle' || event.key === 'lastDateRange') {
        console.log(`BaseChart: Изменение в localStorage: ${event.key}`);
        
        if (event.key === 'lastSelectedVehicle' && event.newValue) {
          try {
            const newVehicle = JSON.parse(event.newValue);
            console.log(`BaseChart: Обновление транспорта из localStorage:`, {
              old: localStorageVehicle ? localStorageVehicle.name : 'нет',
              new: newVehicle.name
            });
            setLocalStorageVehicle(newVehicle);
          } catch (e) {
            console.error('BaseChart: Ошибка при парсинге данных о транспорте из storage event:', e);
          }
        }
        
        if (event.key === 'lastDateRange' && event.newValue) {
          try {
            const newDateRange = JSON.parse(event.newValue);
            console.log(`BaseChart: Обновление диапазона дат из localStorage:`, {
              old: localStorageDateRange ? `${localStorageDateRange.startDate} - ${localStorageDateRange.endDate}` : 'нет',
              new: `${newDateRange.startDate} - ${newDateRange.endDate}`
            });
            setLocalStorageDateRange(newDateRange);
          } catch (e) {
            console.error('BaseChart: Ошибка при парсинге данных о диапазоне дат из storage event:', e);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [localStorageVehicle, localStorageDateRange]);

  // Функция загрузки данных (объявляем здесь, чтобы использовать в useEffect ниже)
  const loadData = useCallback(async (customVehicle, customStartDate, customEndDate) => {
    // Проверяем, не выполняется ли уже загрузка
    if (loadingRef.current) {
      console.log('BaseChart: Пропускаем loadData, т.к. уже выполняется загрузка');
      return;
    }
    
    const vehicleToUse = customVehicle || vehicle || localStorageVehicle;
    const startDateToUse = customStartDate || startDate || (localStorageDateRange ? localStorageDateRange.startDate : null);
    const endDateToUse = customEndDate || endDate || (localStorageDateRange ? localStorageDateRange.endDate : null);
    
    if (!vehicleToUse || !startDateToUse || !endDateToUse) {
      setError('Не выбран транспорт или период');
      return;
    }

    // Создаем строку параметров для проверки дублирования
    const paramsKey = `${vehicleToUse.id || 'no-vehicle'}-${startDateToUse || 'no-start'}-${endDateToUse || 'no-end'}`;
    
    // Проверяем, не такие же ли параметры мы уже загружали
    if (lastLoadParamsRef.current === paramsKey && !customVehicle && !customStartDate && !customEndDate) {
      console.log('BaseChart: Пропускаем загрузку, параметры не изменились');
      return;
    }

    const validatedDates = validateDateRange(startDateToUse, endDateToUse);
    
    // Устанавливаем флаг загрузки
    loadingRef.current = true;
    lastLoadParamsRef.current = paramsKey;
    setIsLoading(true);
    setError(null);

    try {
      console.log(`BaseChart: Загрузка данных для графика ${title}:`, {
        vehicle: vehicleToUse.name,
        id: vehicleToUse.id,
        imei: vehicleToUse.imei,
        startDate: validatedDates.startDate,
        endDate: validatedDates.endDate
      });
      
      const result = await fetchData(vehicleToUse, validatedDates.startDate, validatedDates.endDate);
      
      // Если fetchData возвращает данные, устанавливаем их
      if (result && (result.datasets || result.labels)) {
        console.log('BaseChart: Получены данные от fetchData, устанавливаем в chartData');
        setChartData(result);
      }
    } catch (err) {
      console.error(`Ошибка при загрузке данных для графика ${title}:`, err);
      setError(`Ошибка при загрузке данных: ${err.message || 'Неизвестная ошибка'}`);
    } finally {
      // Сбрасываем флаг загрузки
      loadingRef.current = false;
      setIsLoading(false);
    }
  }, [vehicle, startDate, endDate, localStorageVehicle, localStorageDateRange, fetchData, title]);

  // Дебаунсированная версия loadData для производительности
  const debouncedLoadData = useCallback(
    debounce(loadData, 300),
    [loadData]
  );

  // Эффект для автоматической загрузки данных при изменении ключевых параметров
  useEffect(() => {
    if (!fetchData) return;
    
    console.log('BaseChart: Сработал эффект автоматической загрузки:', {
      hasVehicle: !!vehicle,
      vehicleName: vehicle?.name,
      vehicleImei: vehicle?.imei,
      hasStartDate: !!startDate,
      startDate: startDate?.toISOString ? startDate.toISOString() : startDate,
      hasEndDate: !!endDate,
      endDate: endDate?.toISOString ? endDate.toISOString() : endDate,
      isLoading: loadingRef.current
    });
    
    // Проверяем, есть ли все необходимые данные для загрузки
    if (vehicle && startDate && endDate) {
      console.log('BaseChart: Автоматическая загрузка при изменении параметров:', {
        vehicleName: vehicle.name,
        vehicleImei: vehicle.imei,
        startDate: startDate.toISOString ? startDate.toISOString() : startDate,
        endDate: endDate.toISOString ? endDate.toISOString() : endDate
      });
      
      // Проверяем, не выполняется ли уже загрузка
      if (!loadingRef.current) {
        debouncedLoadData();
      } else {
        console.log('BaseChart: Пропускаем загрузку - уже выполняется');
      }
    } else {
      console.log('BaseChart: Автоматическая загрузка пропущена - недостаточно данных');
    }
  }, [vehicle, startDate, endDate, fetchData, debouncedLoadData]);

  // Создание и настройка данных для графика с мемоизацией
  const memoizedChartData = useMemo(() => {
    if (data === null || data === undefined) {
      return null;
    }

    // Проверяем, передан ли уже готовый config для графика
    if (data.datasets) {
      return data;
    }

    // Старая логика для обратной совместимости
    if (!labels || !data) {
      return null;
    }

    // Создаем градиент для фона (если включено)
    let backgroundColorConfig = backgroundGradient 
      ? function(context) {
          if (!context.chart.chartArea) {
            return null;
          }
          const ctx = context.chart.ctx;
          // Добавляем дополнительную защиту от ошибок
          if (!ctx || !context.chart.height) {
            return 'rgba(0, 0, 0, 0)';
          }
          const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
          const colorRgb = color.replace('rgb', 'rgba').replace(')', ', 0.5)');
          gradient.addColorStop(0, colorRgb);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
          return gradient;
        }
      : 'rgba(0, 0, 0, 0)';

    // Формируем конфигурацию для графика
    return {
      labels,
      datasets: [
        {
          label: title,
          data: data,
          fill: backgroundGradient,
          backgroundColor: backgroundColorConfig,
          borderColor: color,
          borderWidth: 2,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointRadius: 0,
          pointHoverRadius: 5,
          tension: 0,
        },
      ],
    };
  }, [title, data, labels, color, backgroundGradient]);

  // Обновляем chartData при изменении мемоизированных данных
  useEffect(() => {
    setChartData(memoizedChartData);
  }, [memoizedChartData]);

  // Переключение полноэкранного режима
  const toggleExpandedMode = useCallback(() => {
    setExpandedMode(!expandedMode);
    setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.update();
        chartRef.current.resize();
      }
    }, 200);
  }, [expandedMode]);

  // Переключение отображения подсказок по клавиатурным сокращениям
  const toggleKeyboardShortcuts = useCallback(() => {
    setShowKeyboardShortcuts(!showKeyboardShortcuts);
  }, [showKeyboardShortcuts]);

  // Получение классов контейнера
  const getContainerClasses = useCallback(() => {
    return `chart-container ${expandedMode ? 'expanded' : ''}`;
  }, [expandedMode]);

  // Обработка ошибок ChartJS
  const handleChartError = useCallback((error) => {
    console.error('BaseChart: Ошибка графика:', error);
    setError(`Ошибка отображения графика: ${error.message}`);
  }, []);

  return {
    // Состояния
    expandedMode,
    setExpandedMode,
    isLoading,
    setIsLoading,
    chartData,
    setChartData,
    error,
    setError,
    showKeyboardShortcuts,
    setShowKeyboardShortcuts,
    showReportChooser,
    setShowReportChooser,
    splitDirection,
    setSplitDirection,
    containerToFill,
    setContainerToFill,
    syncGroupId,
    setSyncGroupId,
    localStorageVehicle,
    setLocalStorageVehicle,
    localStorageDateRange,
    setLocalStorageDateRange,

    // Рефы
    chartRef,
    chartContainerRef,
    containerId,

    // Функции
    loadData,
    debouncedLoadData,
    toggleExpandedMode,
    toggleKeyboardShortcuts,
    getContainerClasses,
    getLocalStorageData,
    handleChartError,
  };
}; 